import { VegaBox, VegaCard, VegaChip, VegaFlex, VegaFont, VegaGrid } from '@globalpayments/vega-react';

import type { HorizonOptOutDisplayRow, HorizonOptOutType, HorizonRegion } from '../../../types/horizonOptOutTracker';
import { chipColors } from '../constants';

interface SummaryTileProps {
  chip?: HorizonRegion | HorizonOptOutType;
  count: number;
  label: string;
  testId: string;
}

interface HorizonOptOutSummaryProps {
  isEnriching: boolean;
  rows: HorizonOptOutDisplayRow[];
}

const getOptOutCounts = (rows: HorizonOptOutDisplayRow[]) =>
  rows.reduce(
    (counts, row) => {
      counts.total += 1;
      counts[row.region] += 1;
      if (row.detailStatus === 'loaded' && row.type) counts[row.type] += 1;
      return counts;
    },
    { 'Global Payments': 0, UK: 0, US: 0, Worth: 0, 'Worth unavailable': 0, total: 0 },
  );

const SummaryTile = ({ chip, count, label, testId }: SummaryTileProps) => (
  <VegaCard backgroundColor="bg-primary" padding="size-16" variant="border">
    <VegaFlex direction="col" gap="size-8">
      {chip ? (
        <VegaChip
          bgColor={chipColors[chip].bgColor}
          chipType="label"
          size="small"
          text={label}
          textColor={chipColors[chip].textColor}
          variant="filled-background"
        />
      ) : (
        <VegaFont as="span" color="text-secondary" variant="font-p2-short">
          {label}
        </VegaFont>
      )}
      <VegaFont as="span" data-testid={testId} variant="font-h4">
        {count}
      </VegaFont>
    </VegaFlex>
  </VegaCard>
);

/** Displays resolved opt-out totals by region and source. */
export const HorizonOptOutSummary = ({ isEnriching, rows }: HorizonOptOutSummaryProps) => {
  const counts = getOptOutCounts(rows);

  return (
    <VegaBox aria-label="Opt-out breakdown">
      <VegaGrid column={{ default: 1, S: 2, L: 6 }} gap="size-12">
        <SummaryTile count={counts.total} label="Total Opt-Outs" testId="total-count" />
        <SummaryTile chip="US" count={counts.US} label="United States" testId="us-count" />
        <SummaryTile chip="UK" count={counts.UK} label="United Kingdom" testId="uk-count" />
        <SummaryTile chip="Worth" count={counts.Worth} label="Worth Triggered" testId="worth-count" />
        <SummaryTile
          chip="Worth unavailable"
          count={counts['Worth unavailable']}
          label="Worth unavailable"
          testId="worth-unavailable-count"
        />
        <SummaryTile
          chip="Global Payments"
          count={counts['Global Payments']}
          label="Global Payments"
          testId="global-payments-count"
        />
      </VegaGrid>
      {isEnriching && (
        <VegaBox margin={{ top: 'size-8' }}>
          <VegaFont as="span" color="text-secondary" variant="font-p2-short">
            Enriching source details. Region and total counts already include every grid row.
          </VegaFont>
        </VegaBox>
      )}
    </VegaBox>
  );
};
